import { isObject, isString } from '@libs/shared';

/**
 * 获取安全的日志数据用于控制台输出，避免直接引用大型数据导致内存溢出从而引发性能问题
 * 比如：大型二进制或大型文本
 * @param data 原始数据
 * @param visited 已访问的对象集合，用于检测循环引用
 * @see g-6157850110
 */
export const getSafeLogData = <T extends any>(
    data: T,
    visited: WeakSet<object> = new WeakSet()
): T | string => {
    // 处理 Uint8Array (项目中相对常用的二进制类型，优先检查以提升性能)
    if (data instanceof Uint8Array) {
        return `Uint8Array(${data.length} bytes)`;
    }

    // 处理 ArrayBuffer (文件读取时相对较为频繁使用)
    if (data instanceof ArrayBuffer) {
        return `ArrayBuffer(${data.byteLength} bytes)`;
    }

    // 处理 DataView (必须在通用 ArrayBufferView 检查之前)
    if (data instanceof DataView) {
        return `DataView(${data.byteLength} bytes)`;
    }

    // 处理其他类型化数组 (Int8Array, Uint16Array, Float32Array 等)
    if (ArrayBuffer.isView(data)) {
        const typedArray = data as any;
        return `${typedArray.constructor.name}(${typedArray.length} elements, ${typedArray.byteLength} bytes)`;
    }

    // 处理 File (必须在 Blob 之前，因为 File extends Blob)
    if (data instanceof File) {
        return `File(${data.size} bytes, name: ${data.name}, type: ${data.type})`;
    }

    // 处理 Blob (使用频率相对较低，放在后面)
    if (data instanceof Blob) {
        return `Blob(${data.size} bytes, type: ${data.type})`;
    }

    // 处理字符串类型的大型数据
    if (isString(data)) {
        // 检测 Data URL 格式：data:[<mediaType>][;base64],<data>
        const dataUrlMatch = data.match(/^data:([^;,]+)(?:;([^,]+))?,(.+)$/);
        if (dataUrlMatch && data.length > 1000) {
            const mediaType = dataUrlMatch[1];
            const encoding = dataUrlMatch[2];
            const actualData = dataUrlMatch[3];

            return `DataURL(${mediaType}, ${actualData?.length} chars, encoding: ${
                encoding || 'unknown'
            }, preview: "${actualData?.substring(0, 100) || 'unknown'}...")`;
        }

        // 处理其他长字符串
        if (data.length > 10000) {
            return `LongString(${data.length} chars, preview: "${data.substring(0, 100)}...")`;
        }

        // 如果是合理长度的字符串，可以直接返回
        return data;
    }

    // 如果是错误，则直接返回错误对象，保留有价值的错误信息
    if (data instanceof Error) {
        return data;
    }

    // 处理数组中包含大型数据的情况
    if (Array.isArray(data)) {
        // 检测循环引用
        if (visited.has(data)) {
            return '[Circular]';
        }
        visited.add(data);
        return data.map((item) => getSafeLogData(item, visited)) as T;
    }

    // 处理对象中包含大型数据的情况
    if (isObject(data)) {
        // 检测循环引用
        if (visited.has(data)) {
            return '[Circular]';
        }
        visited.add(data);
        const safeObj: Record<string, any> = {};
        for (const [key, value] of Object.entries(data)) {
            safeObj[key] = getSafeLogData(value, visited);
        }
        return safeObj as T;
    }

    // 其他数据类型直接返回
    return data;
};
